import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Calendar, Clock, MapPin, Music, Trash2, Filter, Users, Inbox, Search, ExternalLink, X, Loader2, ListMusic, CheckSquare, Square, LayoutGrid, List, QrCode, ArrowUpDown, Flag, Lock, Unlock, Archive } from 'lucide-react';
import QrPrintModal from '../../components/QrPrintModal';
import { useAuth } from '../../context/AuthContext';
import { useActivePlayback } from '../../context/ActivePlaybackContext';
import { getEventsByDjId, deleteEvent, computeEventStatus, updateEvent, lockEvent, unlockEvent, archiveEvent, Event } from '../../services/events';
import { getRequestsByEventId } from '../../services/requests';
import { effectiveCardColumns, widthTierOf, type DashboardWidthTier } from '../../utils/dashboardColumns';
import toast from 'react-hot-toast';

const SM_QUERY = '(min-width: 640px)';
const LG_QUERY = '(min-width: 1024px)';

function currentWidthTier(): DashboardWidthTier {
  return widthTierOf(window.matchMedia(SM_QUERY).matches, window.matchMedia(LG_QUERY).matches);
}

type DateFilter = 'all' | 'today' | 'upcoming' | 'completed' | 'flagged';

interface EventStats {
  pendingCount: number;
  queuedCount: number;
  playedCount: number;
  participantCount: number;
  unmatchedCount: number;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { activePlayback, unlockPlayback } = useActivePlayback();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const [searchQuery, setSearchQuery] = useState('');
  const [eventStats, setEventStats] = useState<Map<string, EventStats>>(new Map());

  // View mode state (card/list + column count)
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => {
    try { return (localStorage.getItem('votebeats_dashboard_view') as 'card' | 'list') || 'card'; } catch { return 'card'; }
  });
  const [cardColumns, setCardColumns] = useState(() => {
    try { return Number(localStorage.getItem('votebeats_dashboard_cols')) || 3; } catch { return 3; }
  });
  const [widthTier, setWidthTier] = useState<DashboardWidthTier>(currentWidthTier);

  useEffect(() => {
    const smQuery = window.matchMedia(SM_QUERY);
    const lgQuery = window.matchMedia(LG_QUERY);
    const updateTier = () => setWidthTier(widthTierOf(smQuery.matches, lgQuery.matches));
    smQuery.addEventListener('change', updateTier);
    lgQuery.addEventListener('change', updateTier);
    return () => {
      smQuery.removeEventListener('change', updateTier);
      lgQuery.removeEventListener('change', updateTier);
    };
  }, []);

  // Bulk selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // QR modal state
  const [qrEvent, setQrEvent] = useState<Event | null>(null);

  // Lock modal state
  const [lockModalEvent, setLockModalEvent] = useState<Event | null>(null);
  const [lockAlsoArchive, setLockAlsoArchive] = useState(false);
  const [lockingEventId, setLockingEventId] = useState<string | null>(null);

  // Mobile focused event overlay state
  const [focusedEvent, setFocusedEvent] = useState<Event | null>(null);

  // Long-press detection for trash icon multi-select
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggered = useRef(false);

  // Parse YYYY-MM-DD as local date (not UTC) to avoid timezone shift
  const parseLocalDate = (dateStr: string): Date => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // Check if a date is "today" in local timezone
  const isToday = (dateStr: string): boolean => {
    const eventDate = parseLocalDate(dateStr);
    const today = new Date();
    return (
      eventDate.getFullYear() === today.getFullYear() &&
      eventDate.getMonth() === today.getMonth() &&
      eventDate.getDate() === today.getDate()
    );
  };

  // Compute "days until" label
  const getDaysLabel = (dateStr: string, status: Event['status']): string | null => {
    if (status === 'completed') return null;
    const eventDate = parseLocalDate(dateStr);
    const today = new Date();
    eventDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffMs = eventDate.getTime() - today.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 1) return `In ${diffDays} days`;
    return `${Math.abs(diffDays)} days ago`;
  };

  // Filter events based on selected filter and search query (archived events never appear on dashboard)
  const filteredEvents = events.filter(event => {
    if (event.archived) return false;
    // Apply date/status filter
    const passesFilter = (() => {
      switch (dateFilter) {
        case 'today':
          return isToday(event.date);
        case 'upcoming':
          return event.status === 'upcoming';
        case 'completed':
          return event.status === 'completed';
        case 'flagged':
          return event.flagged === true;
        default:
          return true;
      }
    })();
    if (!passesFilter) return false;

    // Apply search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        event.name.toLowerCase().includes(q) ||
        (event.location?.toLowerCase().includes(q) ?? false)
      );
    }
    return true;
  });

  // Sort events
  const sortedFilteredEvents = [...filteredEvents].sort((a, b) => {
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    }
    // Default: sort by date descending (newest first)
    const dateA = new Date(a.date + 'T' + a.startTime);
    const dateB = new Date(b.date + 'T' + b.startTime);
    return dateB.getTime() - dateA.getTime();
  });

  useEffect(() => {
    const loadEvents = async () => {
      if (!user) { setLoading(false); return; }

      const userEvents = await getEventsByDjId(user.uid);
      const sortedEvents = [...userEvents].sort((a, b) => {
        const dateA = new Date(a.date + 'T' + a.startTime);
        const dateB = new Date(b.date + 'T' + b.startTime);
        return dateB.getTime() - dateA.getTime();
      });
      setEvents(sortedEvents);

      // Load requests for non-completed events to compute stats
      const activeEvents = sortedEvents.filter(e => e.status !== 'completed');
      const statsMap = new Map<string, EventStats>();

      await Promise.all(activeEvents.map(async (event) => {
        try {
          const requests = await getRequestsByEventId(event.id);
          const pending = requests.filter(r => r.status === 'pending' && !r.superRequest);
          const queued = requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying');
          const played = requests.filter(r => r.status === 'played');
          const uniqueAttendees = new Set(requests.map(r => r.requestedBy.oderId));
          const unmatched = queued.filter(r => !r.platformLink);

          statsMap.set(event.id, {
            pendingCount: pending.length,
            queuedCount: queued.length,
            playedCount: played.length,
            participantCount: uniqueAttendees.size,
            unmatchedCount: unmatched.length,
          });
        } catch {
          // Silently skip failed event loads
        }
      }));

      // Also load stats for completed events (but not their pending — those won't be processed)
      const completedEvents = sortedEvents.filter(e => e.status === 'completed');
      await Promise.all(completedEvents.map(async (event) => {
        try {
          const requests = await getRequestsByEventId(event.id);
          const queued = requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying');
          const played = requests.filter(r => r.status === 'played');
          const uniqueAttendees = new Set(requests.map(r => r.requestedBy.oderId));
          statsMap.set(event.id, {
            pendingCount: requests.filter(r => r.status === 'pending' && !r.superRequest).length,
            queuedCount: queued.length,
            playedCount: played.length,
            participantCount: uniqueAttendees.size,
            unmatchedCount: queued.filter(r => !r.platformLink).length,
          });
        } catch {
          // Skip
        }
      }));

      setEventStats(statsMap);
      setLoading(false);
    };
    loadEvents();
  }, [user]);

  // Auto-update event statuses every 60 seconds based on time
  useEffect(() => {
    if (events.length === 0) return;

    const checkStatuses = async () => {
      let updated = false;
      const newEvents = events.map(event => {
        if (event.status === 'completed') return event;
        const computed = computeEventStatus(event);
        if (computed !== event.status) {
          updated = true;
          // Persist to Firestore (fire-and-forget)
          updateEvent(event.id, { status: computed }).catch(() => {});
          return { ...event, status: computed };
        }
        return event;
      });
      if (updated) setEvents(newEvents);
    };

    const interval = setInterval(checkStatuses, 60000);
    return () => clearInterval(interval);
  }, [events]);

  const handleDeleteEvent = async (eventId: string, eventName: string) => {
    if (!confirm(`Are you sure you want to delete "${eventName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await deleteEvent(eventId);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Event deleted successfully');
    } catch {
      toast.error('Failed to delete event');
    }
  };

  const handleToggleFlag = async (eventId: string) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    const newFlagged = !event.flagged;
    try {
      await updateEvent(eventId, { flagged: newFlagged });
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, flagged: newFlagged } : e));
    } catch {
      toast.error('Failed to update flag');
    }
  };

  const handleLockConfirm = async () => {
    if (!lockModalEvent) return;
    setLockingEventId(lockModalEvent.id);
    try {
      if (lockAlsoArchive) {
        await archiveEvent(lockModalEvent.id);
        setEvents(prev => prev.map(e => e.id === lockModalEvent.id ? { ...e, locked: true, archived: true } : e));
        toast.success('Event locked and archived');
      } else {
        await lockEvent(lockModalEvent.id);
        setEvents(prev => prev.map(e => e.id === lockModalEvent.id ? { ...e, locked: true } : e));
        toast.success('Event locked');
      }
    } catch {
      toast.error('Failed to lock event');
    } finally {
      setLockingEventId(null);
      setLockModalEvent(null);
      setLockAlsoArchive(false);
    }
  };

  const handleUnlock = async (eventId: string) => {
    if (!confirm('Unlock this event for editing?')) return;
    setLockingEventId(eventId);
    try {
      await unlockEvent(eventId);
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, locked: false } : e));
      toast.success('Event unlocked');
    } catch {
      toast.error('Failed to unlock event');
    } finally {
      setLockingEventId(null);
    }
  };

  const handleArchive = async (eventId: string) => {
    if (!confirm('Archive this event? It will be moved to the Archive page.')) return;
    setLockingEventId(eventId);
    try {
      await archiveEvent(eventId);
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, locked: true, archived: true } : e));
      toast.success('Event archived');
    } catch {
      toast.error('Failed to archive event');
    } finally {
      setLockingEventId(null);
    }
  };

  const toggleSelectEvent = (eventId: string) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const count = selectedEventIds.size;
    if (count === 0) return;
    const selectedNames = events.filter(e => selectedEventIds.has(e.id)).map(e => e.name);
    if (!confirm(`Delete ${count} event${count > 1 ? 's' : ''}?\n\n${selectedNames.join('\n')}\n\nThis cannot be undone.`)) {
      return;
    }
    setBulkDeleting(true);
    let deleted = 0;
    for (const eventId of selectedEventIds) {
      try {
        await deleteEvent(eventId);
        deleted++;
        if (count > 2) toast.loading(`Deleting ${deleted} of ${count}...`, { id: 'bulk-delete' });
      } catch {
        toast.error(`Failed to delete event`);
      }
    }
    toast.dismiss('bulk-delete');
    setEvents(prev => prev.filter(e => !selectedEventIds.has(e.id)));
    setSelectedEventIds(new Set());
    setSelectionMode(false);
    setBulkDeleting(false);
    toast.success(`${deleted} event${deleted > 1 ? 's' : ''} deleted`);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedEventIds(new Set());
  };

  const isMobile = () => !window.matchMedia(SM_QUERY).matches;

  const handleEventCardClick = (evt: Event, e: React.MouseEvent) => {
    if (selectionMode) {
      e.preventDefault();
      return;
    }
    if (isMobile()) {
      e.preventDefault();
      setFocusedEvent(evt);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: Event['status']) => {
    switch (status) {
      case 'upcoming':
        return <span className="badge badge-info">Upcoming</span>;
      case 'active':
        return <span className="badge badge-success">Active</span>;
      case 'completed':
        return <span className="badge badge-neutral">Completed</span>;
    }
  };

  // --- Long-press trash icon for multi-select ---
  const handleTrashPointerDown = (eventId: string, e: React.PointerEvent) => {
    e.stopPropagation();
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      setSelectionMode(true);
      setSelectedEventIds(new Set([eventId]));
    }, 500);
  };

  const handleTrashPointerUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTrashClick = (eventId: string, eventName: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (longPressTriggered.current) {
      longPressTriggered.current = false;
      return;
    }
    handleDeleteEvent(eventId, eventName);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="skeleton h-8 w-32 mb-2"></div>
            <div className="skeleton h-4 w-48"></div>
          </div>
          <div className="skeleton h-10 w-32 rounded-full"></div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-6">
              <div className="skeleton h-6 w-3/4 mb-4"></div>
              <div className="space-y-2">
                <div className="skeleton h-4 w-1/2"></div>
                <div className="skeleton h-4 w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your DJ events</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/events/create" className="btn btn-primary">
            <Plus className="w-5 h-5 mr-2" />
            Create Event
          </Link>
        </div>
      </div>

      {/* Date Filter Tabs + Select Mode */}
      {events.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
          {(['all', 'today', 'upcoming', 'completed', 'flagged'] as DateFilter[]).map((filter) => (
            <button
              key={filter}
              onClick={() => setDateFilter(filter)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                dateFilter === filter
                  ? filter === 'flagged' ? 'bg-orange-500 text-white' : 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {filter === 'all' && 'All Events'}
              {filter === 'today' && 'Today'}
              {filter === 'upcoming' && 'Upcoming'}
              {filter === 'completed' && 'Completed'}
              {filter === 'flagged' && (<><Flag className="w-3 h-3 inline mr-1" />Flagged</>)}
              {filter === dateFilter && ` (${filteredEvents.length})`}
            </button>
          ))}
          <div className="flex-1" />
          {selectionMode ? (
            <button
              onClick={exitSelectionMode}
              className="px-3 py-1.5 rounded-full text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
          ) : (
            <button
              onClick={() => setSelectionMode(true)}
              className="px-3 py-1.5 rounded-full text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              Select
            </button>
          )}
          <div className="w-px h-5 bg-gray-300 dark:bg-gray-700" />
          <div className="flex items-center gap-1">
            <button
              onClick={() => { setViewMode('card'); localStorage.setItem('votebeats_dashboard_view', 'card'); }}
              className={`p-1.5 rounded transition-colors ${viewMode === 'card' ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
              title="Card view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setViewMode('list'); localStorage.setItem('votebeats_dashboard_view', 'list'); }}
              className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          {viewMode === 'card' && (
            <input
              type="range"
              min={2}
              max={5}
              value={cardColumns}
              onChange={(e) => {
                const v = Number(e.target.value);
                setCardColumns(v);
                localStorage.setItem('votebeats_dashboard_cols', String(v));
              }}
              className="w-20 accent-primary hidden lg:block"
              title={`${cardColumns} columns`}
            />
          )}
        </div>
      )}

      {/* Search & Sort Controls */}
      {events.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-9 py-2 text-sm w-full"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setSortBy(sortBy === 'date' ? 'name' : 'date')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
            title={`Sort by ${sortBy === 'date' ? 'name' : 'date'}`}
          >
            <ArrowUpDown className="w-4 h-4" />
            {sortBy === 'date' ? 'Date' : 'Name'}
          </button>
        </div>
      )}

      {events.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Music className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Events Yet
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Create your first event to start accepting song requests from your audience.
          </p>
          <Link to="/events/create" className="btn btn-primary inline-flex items-center">
            <Plus className="w-5 h-5 mr-2" />
            Create Your First Event
          </Link>
        </div>
      ) : sortedFilteredEvents.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="w-12 h-12 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
            <Calendar className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            No {dateFilter === 'today' ? "Today's" : dateFilter === 'upcoming' ? 'Upcoming' : dateFilter === 'flagged' ? 'Flagged' : 'Completed'} Events
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {dateFilter === 'today' && "You don't have any events scheduled for today."}
            {dateFilter === 'upcoming' && "You don't have any upcoming events."}
            {dateFilter === 'completed' && "You don't have any completed events yet."}
            {dateFilter === 'flagged' && "You haven't flagged any events. Use the flag icon on event cards to mark important events."}
          </p>
        </div>
      ) : viewMode === 'list' ? (
          <div className="space-y-2">
            {sortedFilteredEvents.map((event) => {
              const stats = eventStats.get(event.id);
              const daysLabel = getDaysLabel(event.date, event.status);
              return (
                <div
                  key={event.id}
                  className={`card px-4 py-3 flex items-center gap-4 hover:border-primary transition-colors group relative ${selectionMode && selectedEventIds.has(event.id) ? 'ring-2 ring-primary border-primary' : ''} ${activePlayback?.eventId === event.id ? 'ring-2 ring-green-400 border-green-400 dark:border-green-500 bg-green-50/50 dark:bg-green-900/10' : ''}`}
                  onClick={selectionMode ? (e) => { e.preventDefault(); toggleSelectEvent(event.id); } : undefined}
                >
                  {selectionMode && (
                    <button onClick={(e) => { e.stopPropagation(); toggleSelectEvent(event.id); }} className="flex-shrink-0">
                      {selectedEventIds.has(event.id) ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-gray-400" />}
                    </button>
                  )}
                  <Link to={`/events/${event.id}/manage`} className="flex flex-col gap-1 2xl:flex-row 2xl:items-center 2xl:gap-4 flex-1 min-w-0" onClick={(e) => handleEventCardClick(event, e)}>
                    <div className="flex items-center gap-2 min-w-0 2xl:contents">
                    {getStatusBadge(event.status)}
                    {event.locked && <span title="Locked"><Lock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" /></span>}
                    <span className="font-semibold text-gray-900 dark:text-white min-w-0 break-words 2xl:truncate" title={event.name}>{event.name}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0 2xl:contents">
                    <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(event.date)}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{event.startTime}{event.endTime ? ` - ${event.endTime}` : ''}</span>
                    {event.createdAt && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        Created {new Date(event.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                    {daysLabel && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                        daysLabel === 'Today' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        daysLabel === 'Tomorrow' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {daysLabel}
                      </span>
                    )}
                    {activePlayback?.eventId === event.id && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (confirm('Stop Spotify listening for this event?')) unlockPlayback(); }}
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 animate-pulse hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
                        title="Click to stop listening"
                      >
                        <Music className="w-3 h-3" /> Listening
                      </button>
                    )}
                    <div className="flex items-center gap-2 2xl:ml-auto flex-shrink-0">
                      {stats && stats.pendingCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                          <Inbox className="w-3 h-3" />{stats.pendingCount}
                        </span>
                      )}
                      {stats && stats.queuedCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                          <ListMusic className="w-3 h-3" />{stats.queuedCount}
                        </span>
                      )}
                      {stats && stats.playedCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                          <Music className="w-3 h-3" />{stats.playedCount}
                        </span>
                      )}
                      {stats && stats.unmatchedCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                          {stats.unmatchedCount} unmatched
                        </span>
                      )}
                    </div>
                    </div>
                  </Link>
                  {!selectionMode && (
                    <>
                    {event.status === 'completed' && (
                      <>
                        {event.locked ? (
                          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUnlock(event.id); }} disabled={lockingEventId === event.id} className="p-1.5 rounded-lg text-amber-500 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" title="Unlock event">
                            <Unlock className="w-4 h-4" />
                          </button>
                        ) : (
                          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLockModalEvent(event); setLockAlsoArchive(false); }} disabled={lockingEventId === event.id} className="p-1.5 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" title="Lock event">
                            <Lock className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleArchive(event.id); }} disabled={lockingEventId === event.id} className="p-1.5 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" title="Archive event">
                          <Archive className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleFlag(event.id); }} className={`p-1.5 rounded-lg transition-opacity flex-shrink-0 ${event.flagged ? 'text-orange-500' : 'text-gray-400 hover:text-orange-500 opacity-0 group-hover:opacity-100'}`} title={event.flagged ? 'Remove flag' : 'Flag event'}>
                      <Flag className="w-4 h-4" fill={event.flagged ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      onPointerDown={(e) => handleTrashPointerDown(event.id, e)}
                      onPointerUp={handleTrashPointerUp}
                      onPointerLeave={handleTrashPointerUp}
                      onClick={(e) => handleTrashClick(event.id, event.name, e)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-danger hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      title="Delete event (long-press to multi-select)"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${effectiveCardColumns(cardColumns, widthTier)}, minmax(0, 1fr))` }}>
          {sortedFilteredEvents.map((event) => {
            const stats = eventStats.get(event.id);
            const daysLabel = getDaysLabel(event.date, event.status);
            return (
              <div
                key={event.id}
                className={`card p-6 hover:border-primary transition-colors group relative ${selectionMode && selectedEventIds.has(event.id) ? 'ring-2 ring-primary border-primary' : ''} ${activePlayback?.eventId === event.id ? 'ring-2 ring-green-400 border-green-400 dark:border-green-500 bg-green-50/50 dark:bg-green-900/10' : ''}`}
                onClick={selectionMode ? (e) => { e.preventDefault(); toggleSelectEvent(event.id); } : undefined}
              >
                {/* Selection checkbox overlay */}
                {selectionMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelectEvent(event.id); }}
                    className="absolute top-3 left-3 z-10 p-1"
                  >
                    {selectedEventIds.has(event.id) ? (
                      <CheckSquare className="w-5 h-5 text-primary" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                )}
                <Link to={`/events/${event.id}/manage`} className="block" onClick={(e) => handleEventCardClick(event, e)}>
                  <h3 className={`text-lg font-semibold text-gray-900 dark:text-white line-clamp-2 break-words ${selectionMode ? 'pl-6' : ''}`} title={event.name}>
                    {event.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1 mb-3">
                    {daysLabel && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        daysLabel === 'Today' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        daysLabel === 'Tomorrow' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {daysLabel}
                      </span>
                    )}
                    {getStatusBadge(event.status)}
                    {event.locked && <span title="Locked"><Lock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" /></span>}
                    {activePlayback?.eventId === event.id && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (confirm('Stop Spotify listening for this event?')) {
                            unlockPlayback();
                          }
                        }}
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 animate-pulse hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
                        title="Click to stop listening"
                      >
                        <Music className="w-3 h-3" /> Listening
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(event.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{event.startTime}{event.endTime ? ` - ${event.endTime}` : ''}</span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}</span>
                      </div>
                    )}
                    {event.createdAt && (
                      <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                        <Plus className="w-3 h-3" />
                        <span>Created {new Date(event.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    )}
                  </div>

                  {/* Event Stats */}
                  {stats && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      {stats.participantCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                          <Users className="w-3 h-3" />
                          {stats.participantCount} participant{stats.participantCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {stats.pendingCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                          <Inbox className="w-3 h-3" />
                          {stats.pendingCount} pending
                        </span>
                      )}
                      {stats.queuedCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                          <ListMusic className="w-3 h-3" />
                          {stats.queuedCount} queued
                        </span>
                      )}
                      {stats.playedCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                          <Music className="w-3 h-3" />
                          {stats.playedCount} played
                        </span>
                      )}
                      {stats.unmatchedCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                          {stats.unmatchedCount} unmatched
                        </span>
                      )}
                    </div>
                  )}
                </Link>
                {!selectionMode && (
                  <>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setQrEvent(event);
                      }}
                      className="absolute bottom-4 right-4 p-2 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="QR Code"
                    >
                      <QrCode className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleFlag(event.id);
                      }}
                      className={`absolute bottom-4 right-12 p-2 rounded-lg transition-opacity ${event.flagged ? 'text-orange-500' : 'text-gray-400 hover:text-orange-500 opacity-0 group-hover:opacity-100'}`}
                      title={event.flagged ? 'Remove flag' : 'Flag event'}
                    >
                      <Flag className="w-4 h-4" fill={event.flagged ? 'currentColor' : 'none'} />
                    </button>
                    {event.status === 'completed' && (
                      <>
                        {event.locked ? (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUnlock(event.id); }}
                            disabled={lockingEventId === event.id}
                            className="absolute bottom-4 right-24 p-2 rounded-lg text-amber-500 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Unlock event"
                          >
                            <Unlock className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLockModalEvent(event); setLockAlsoArchive(false); }}
                            disabled={lockingEventId === event.id}
                            className="absolute bottom-4 right-24 p-2 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Lock event"
                          >
                            <Lock className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleArchive(event.id); }}
                          disabled={lockingEventId === event.id}
                          className="absolute bottom-4 right-36 p-2 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Archive event"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onPointerDown={(e) => handleTrashPointerDown(event.id, e)}
                      onPointerUp={handleTrashPointerUp}
                      onPointerLeave={handleTrashPointerUp}
                      onClick={(e) => handleTrashClick(event.id, event.name, e)}
                      className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-danger hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete event (long-press to multi-select)"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
          </div>
        )}

      {/* Bulk Selection Action Bar */}
      {selectionMode && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)]">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedEventIds.size} selected
              </span>
              <button
                onClick={() => setSelectedEventIds(new Set(sortedFilteredEvents.map(e => e.id)))}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                Select All
              </button>
              <button
                onClick={() => setSelectedEventIds(new Set())}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-medium"
              >
                Select None
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exitSelectionMode}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={selectedEventIds.size === 0 || bulkDeleting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {bulkDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Focused Event Overlay */}
      {focusedEvent && (() => {
        const stats = eventStats.get(focusedEvent.id);
        const daysLabel = getDaysLabel(focusedEvent.date, focusedEvent.status);
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center sm:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setFocusedEvent(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full mx-4 max-w-md max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  {getStatusBadge(focusedEvent.status)}
                  {daysLabel && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      daysLabel === 'Today' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      daysLabel === 'Tomorrow' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>{daysLabel}</span>
                  )}
                </div>
                <button onClick={() => setFocusedEvent(null)} className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{focusedEvent.name}</h2>

                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(focusedEvent.date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{focusedEvent.startTime}{focusedEvent.endTime ? ` - ${focusedEvent.endTime}` : ''}</span>
                  </div>
                  {focusedEvent.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{focusedEvent.location}</span>
                    </div>
                  )}
                </div>

                {/* Stats */}
                {stats && (
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-blue-500" />
                      <span className="text-gray-700 dark:text-gray-300">{stats.participantCount} participants</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Inbox className="w-4 h-4 text-amber-500" />
                      <span className="text-gray-700 dark:text-gray-300">{stats.pendingCount} pending</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <ListMusic className="w-4 h-4 text-green-500" />
                      <span className="text-gray-700 dark:text-gray-300">{stats.queuedCount} queued</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Music className="w-4 h-4 text-purple-500" />
                      <span className="text-gray-700 dark:text-gray-300">{stats.playedCount} played</span>
                    </div>
                  </div>
                )}

                {/* Spotify listening indicator */}
                {activePlayback?.eventId === focusedEvent.id && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm text-green-700 dark:text-green-400">
                    <Music className="w-4 h-4 animate-pulse" />
                    <span>Spotify is listening on this event</span>
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => { setQrEvent(focusedEvent); }}
                  className="btn btn-secondary flex-1 flex items-center justify-center gap-2 text-sm"
                >
                  <QrCode className="w-4 h-4" /> QR Code
                </button>
                <Link
                  to={`/events/${focusedEvent.id}/manage`}
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2 text-sm"
                >
                  <ExternalLink className="w-4 h-4" /> Open Event
                </Link>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Lock Event Modal */}
      {lockModalEvent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lock Event</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This event will become view-only. All editing will be disabled.
            </p>
            <label className="flex items-center gap-3 cursor-pointer mb-6 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <input
                type="checkbox"
                checked={lockAlsoArchive}
                onChange={(e) => setLockAlsoArchive(e.target.checked)}
                className="w-4 h-4 rounded accent-primary"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Also archive this event</span>
            </label>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setLockModalEvent(null); setLockAlsoArchive(false); }} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleLockConfirm} disabled={lockingEventId === lockModalEvent.id} className="btn btn-primary flex items-center gap-2">
                {lockingEventId === lockModalEvent.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Lock Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Print Modal */}
      {qrEvent && (
        <QrPrintModal
          eventName={qrEvent.name}
          eventDate={qrEvent.date}
          startTime={qrEvent.startTime}
          endTime={qrEvent.endTime}
          eventUrl={qrEvent.eventUrl || qrEvent.id}
          location={qrEvent.location}
          address={qrEvent.address}
          contactInfo={qrEvent.contactInfo}
          onClose={() => setQrEvent(null)}
        />
      )}
    </div>
  );
}
